import { useCallback, useMemo } from 'react'
import {
  genericHandleChange,
  genericHandleNestedFields,
  genericHandleNestedNestedFields,
  giveGenericHandleChangedData,
} from 'src/consumer/helpers/utilFunctions'
import { usePrescriptionBodyHandlers } from './usePrescriptionBodyHandlers'

/**
 * Custom hook for prescription template change handlers
 * Consolidates all handler logic in one place following DRY principle
 */
export function usePrescriptionTemplateHandlers(setPrescriptionTemplate) {
  // Get body-specific handlers
  const bodyHandlers = usePrescriptionBodyHandlers(setPrescriptionTemplate)
  // Main change handler
  const handleChange = useCallback(
    (e) => {
      genericHandleChange(e, setPrescriptionTemplate)
    },
    [setPrescriptionTemplate],
  )

  // Generic nested field handler
  const handleNestedChange = useCallback(
    (e, outerId) => {
      genericHandleNestedFields(e, outerId, setPrescriptionTemplate)
    },
    [setPrescriptionTemplate],
  )

  // Generic nested nested field handler
  const handleNestedNestedChange = useCallback(
    (e, outerId, outerOuterId) => {
      genericHandleNestedNestedFields(e, outerId, outerOuterId, setPrescriptionTemplate)
    },
    [setPrescriptionTemplate],
  )

  // Header-specific change handler
  const handleChangeHeader = useCallback(
    (e) => {
      handleNestedChange(e, 'header')
    },
    [handleNestedChange],
  )

  // Footer-specific change handler
  const handleChangeFooter = useCallback(
    (e) => {
      handleNestedChange(e, 'footer')
    },
    [handleNestedChange],
  )

  // Left sidebar change handler
  const handleChangeLeftSidebar = useCallback(
    (e) => {
      handleNestedChange(e, 'leftSidebar')
    },
    [handleNestedChange],
  )

  // Right sidebar change handler
  const handleChangeRightSidebar = useCallback(
    (e) => {
      handleNestedChange(e, 'rightSidebar')
    },
    [handleNestedChange],
  )

  // Margins change handler
  const handleChangeMargins = useCallback(
    (e) => {
      handleNestedChange(e, 'margins')
    },
    [handleNestedChange],
  )

  // Second doctor info change handler
  const handleChangeSecondDoctor = useCallback(
    (e) => {
      setPrescriptionTemplate((prev) => {
        const formedObject = giveGenericHandleChangedData(e, prev?.otherDoctorInfos?.[0])
        return {
          ...prev,
          otherDoctorInfos: [formedObject],
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Related clinic details change handler (for multi-column footers)
  const handleChangeRelatedClinicDetails = useCallback(
    (e, idx) => {
      setPrescriptionTemplate((prev) => {
        const relatedClinicDetails = prev?.relatedClinicDetails || []
        const finalArray = Array.from(
          {
            length: Math.max(relatedClinicDetails.length, idx + 1),
          },
          (_, index) => relatedClinicDetails[index] || {},
        )

        return {
          ...prev,
          relatedClinicDetails: finalArray.map((details, index) =>
            index === idx ? giveGenericHandleChangedData(e, details) : details,
          ),
        }
      })
    },
    [setPrescriptionTemplate],
  )

  // Signature details (signatureDetails object + legacy signature fallback)
  const handleChangeSignatureDetails = useCallback(
    (e) => {
      setPrescriptionTemplate((prev) => {
        const nextDetails = giveGenericHandleChangedData(e, prev?.signatureDetails || {})
        const isImageId = e?.target?.id === 'imageId'
        return {
          ...prev,
          signatureDetails: nextDetails,
          ...(isImageId ? { signature: nextDetails.imageId ?? prev?.signature } : {}),
        }
      })
    },
    [setPrescriptionTemplate],
  )

  const handleSignatureRichTextChange = useCallback(
    (htmlString) => {
      setPrescriptionTemplate((prev) => ({
        ...prev,
        signatureDetails: {
          ...(prev?.signatureDetails || {}),
          richText: htmlString ?? '',
        },
      }))
    },
    [setPrescriptionTemplate],
  )

  // Memoize the return object to prevent unnecessary re-renders
  // Without this, every render creates a new object reference, causing all child components to re-render
  return useMemo(
    () => ({
      handleChange,
      handleNestedChange,
      handleNestedNestedChange,
      handleChangeHeader,
      handleChangeFooter,
      handleChangeLeftSidebar,
      handleChangeRightSidebar,
      handleChangeMargins,
      handleChangeSecondDoctor,
      handleChangeRelatedClinicDetails,
      handleChangeSignatureDetails,
      handleSignatureRichTextChange,
      // Body handlers
      handleBodyBooleanChange: bodyHandlers.handleBodyBooleanChange,
      handleBodyEmrSectionStyleChange: bodyHandlers.handleBodyEmrSectionStyleChange,
      createSetOverrideData: bodyHandlers.createSetOverrideData,
      setPrescriptionTemplate,
    }),
    [
      handleChange,
      handleNestedChange,
      handleNestedNestedChange,
      handleChangeHeader,
      handleChangeFooter,
      handleChangeLeftSidebar,
      handleChangeRightSidebar,
      handleChangeMargins,
      handleChangeSecondDoctor,
      handleChangeRelatedClinicDetails,
      handleChangeSignatureDetails,
      handleSignatureRichTextChange,
      bodyHandlers.handleBodyBooleanChange,
      bodyHandlers.handleBodyEmrSectionStyleChange,
      bodyHandlers.createSetOverrideData,
      setPrescriptionTemplate,
    ],
  )
}
